import { toast } from "@/components/ui";
import { QueryKeys } from "@/constants/Query";
import type {
  WaitingRoomEncounter,
  WaitingRoomPatient,
} from "@/features/LiveQueue/network/useWaitingRoomDashboard";
import type {
  PatientTabsResponse,
  PatientTabsResponse_Tab,
} from "@/features/PatientTabs/network/usePatientTabs";
import { PatientTabType } from "@/features/PatientTabs/network/usePatientTabs";
import { useAddPatientTab } from "@/network/useAddPatientTab";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

export const useOpenPatientChartPreview = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return ({
    patient,
    encounter,
  }: { patient: WaitingRoomPatient; encounter: WaitingRoomEncounter }) => {
    queryClient.setQueryData<PatientTabsResponse>(
      QueryKeys.Provider.PATIENT_TABS,
      (oldData) => {
        const newTab: PatientTabsResponse_Tab = {
          patient: {
            id: patient.id,
            nameFirst: patient.nameFirst,
            nameLast: patient.nameLast,
            dob: patient.dob,
          },
          encounter: {
            id: encounter.id,
            encounterClass: "",
            condition: "",
          },
          sla: null,
          tabType: PatientTabType.PREVIEW,
          addedAt: new Date().toISOString(),
        };

        if (!oldData) {
          return { patientTabs: [newTab] };
        }

        return {
          ...oldData,
          patientTabs: [newTab, ...oldData.patientTabs],
        };
      },
    );
    navigate({
      to: "/patient/$patientId/timeline",
      params: {
        patientId: patient.id,
      },
      search: {
        encounterId: encounter.id,
      },
    });
  };
};

export const useOpenPatientChart = () => {
  const navigate = useNavigate();
  const addPatientTab = useAddPatientTab();

  return async ({
    patientId,
    encounterId,
    medRecordDestination,
    chatTab,
  }: {
    patientId: string;
    encounterId?: string;
    isPreview?: boolean;
    medRecordDestination?: string;
    chatTab?: "live" | "async" | "combined";
  }) => {
    try {
      await addPatientTab.mutateAsync({ patientId });

      const search = {} as Record<string, string>;

      if (encounterId) {
        search.encounterId = encounterId;
      }

      if (chatTab) {
        search.chatTab = chatTab;
      }

      navigate({
        to: medRecordDestination
          ? `/patient/$patientId/${medRecordDestination}`
          : "/patient/$patientId",
        params: {
          patientId,
        },
        search,
      });
    } catch (error) {
      toast.error("Unable to open chart", {
        id: "add-patient-tab-error",
      });
    }
  };
};
